/**
 * Dev-only mock layer for the loyalty flow.
 *
 * Activate by appending ?mockLoyalty=1 (or =fresh / =enrolled) to the portal
 * URL. When active:
 *   - loyaltyApi.getProgram / signup return canned data instead of calling
 *     the backend (no DB writes, no Stripe Checkout Session, no charge).
 *   - getProgram returns a non-null program, which is what gates the entire
 *     loyalty surface (card, modal, nav, signup page). So any reservation
 *     can see the loyalty UI for QA even if the host has no real program.
 *   - EmbeddedPayment renders a placeholder with "Simulate success / cancel"
 *     buttons instead of mounting the real Stripe Embedded Checkout iframe.
 *
 * Gated on import.meta.env.DEV so production builds never honor the flag.
 *
 * Modes:
 *   off       no URL flag, or not running dev build
 *   fresh     guest has not signed up yet (card + modal visible, payment
 *             stage reachable)
 *   enrolled  guest is already a member (card flips to "Member", modal is
 *             suppressed, nav badge shows)
 */

import type { LoyaltyEmbeddedCheckoutSession, LoyaltyProgramResponse } from '@/types';

export type LoyaltyMockMode = 'off' | 'fresh' | 'enrolled';

const MOCK_CLIENT_SECRET_PREFIX = 'mock_loyalty_';
const STORAGE_KEY = 'mockLoyalty';

function parseFlag(raw: string | null): LoyaltyMockMode {
  if (raw === 'enrolled') return 'enrolled';
  if (raw === '1' || raw === 'fresh' || raw === 'true') return 'fresh';
  return 'off';
}

function readQuery(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('mockLoyalty');
}

// Persist the URL flag into localStorage so it survives the auth redirect
// (lookup -> verify -> back to portal strips query params) and HMR reloads.
// Set with ?mockLoyalty=1 (or fresh|enrolled) once; reset with ?mockLoyalty=off.
export function getLoyaltyMockMode(): LoyaltyMockMode {
  if (!import.meta.env.DEV) return 'off';
  if (typeof window === 'undefined') return 'off';

  const fromUrl = readQuery();
  if (fromUrl !== null) {
    if (fromUrl === 'off' || fromUrl === '0' || fromUrl === 'false') {
      window.localStorage.removeItem(STORAGE_KEY);
      return 'off';
    }
    const parsed = parseFlag(fromUrl);
    if (parsed !== 'off') window.localStorage.setItem(STORAGE_KEY, parsed);
    return parsed;
  }
  return parseFlag(window.localStorage.getItem(STORAGE_KEY));
}

export function isLoyaltyMockActive(): boolean {
  return getLoyaltyMockMode() !== 'off';
}

export function isLoyaltyMockClientSecret(s: string): boolean {
  return s.startsWith(MOCK_CLIENT_SECRET_PREFIX);
}

const mockProgram = {
  id: 999001,
  tenantUserId: 0,
  name: 'Ocean City Insiders',
  description: 'Test-mode fixture. Not real.',
  config: {
    kind: 'tiered_subscription_v1' as const,
    targetCatalog: {
      booking_total: [{ kind: 'booking_total' as const }],
      beach_equipment: [{ kind: 'custom_upsell' as const, upsellIds: [3, 4, 5, 6] }],
      annual_credit: [{ kind: 'custom_upsell' as const, upsellIds: [3, 4, 5, 6] }],
    },
    tiers: [
      {
        key: 'low',
        label: 'Low Tide',
        priceCents: 9900,
        currency: 'usd',
        interval: 'year' as const,
        perks: [
          { type: 'discount' as const, benefitKey: 'booking_total', value: { mode: 'percent' as const, amount: 5 } },
          { type: 'discount' as const, benefitKey: 'beach_equipment', value: { mode: 'percent' as const, amount: 25 } },
          { type: 'free_night' as const, thresholdNights: 20 },
          { type: 'stored_credit' as const, benefitKey: 'annual_credit', amountCents: 5000, cycle: 'annual' as const, appliesTo: ['beach_equipment'] },
        ],
      },
      {
        key: 'rising',
        label: 'Rising Tide',
        priceCents: 24900,
        currency: 'usd',
        interval: 'year' as const,
        perks: [
          { type: 'discount' as const, benefitKey: 'booking_total', value: { mode: 'percent' as const, amount: 10 } },
          { type: 'discount' as const, benefitKey: 'beach_equipment', value: { mode: 'percent' as const, amount: 50 } },
          { type: 'free_night' as const, thresholdNights: 15 },
          { type: 'stored_credit' as const, benefitKey: 'annual_credit', amountCents: 12500, cycle: 'annual' as const, appliesTo: ['beach_equipment'] },
        ],
      },
      {
        key: 'high',
        label: 'High Tide',
        priceCents: 49900,
        currency: 'usd',
        interval: 'year' as const,
        perks: [
          { type: 'discount' as const, benefitKey: 'booking_total', value: { mode: 'percent' as const, amount: 10 } },
          { type: 'discount' as const, benefitKey: 'beach_equipment', value: { mode: 'free' as const } },
          { type: 'free_night' as const, thresholdNights: 10 },
          { type: 'stored_credit' as const, benefitKey: 'annual_credit', amountCents: 25000, cycle: 'annual' as const, appliesTo: ['beach_equipment'] },
        ],
      },
    ],
  },
  enabled: true,
};

export function mockGetProgram(): LoyaltyProgramResponse {
  const mode = getLoyaltyMockMode();
  return {
    program: mockProgram,
    enrolledMembership:
      mode === 'enrolled'
        ? { id: 999002, programId: mockProgram.id, status: 'active', currentTierKey: 'rising' }
        : null,
    prefill: {
      firstName: 'Mock',
      lastName: 'Guest',
      email: 'mock.guest@example.com',
      phone: null,
    },
  };
}

export function mockSignup(): LoyaltyEmbeddedCheckoutSession {
  return {
    clientSecret: `${MOCK_CLIENT_SECRET_PREFIX}${Date.now()}`,
    connectedAccountId: 'acct_mock',
    membershipId: 999002,
  };
}

// Boot-time side effect: persist any ?mockLoyalty=... URL flag to localStorage
// as soon as this module is imported (api.ts pulls it in transitively from
// main.tsx). This is what makes the flag survive the auth redirect from
// /portal/lookup -> verify -> /portal/home: no loyalty code path runs on the
// lookup page, so without this we'd miss the URL flag entirely.
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  const fromUrl = readQuery();
  if (fromUrl !== null) {
    if (fromUrl === 'off' || fromUrl === '0' || fromUrl === 'false') {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      const parsed = parseFlag(fromUrl);
      if (parsed !== 'off') window.localStorage.setItem(STORAGE_KEY, parsed);
    }
  }
}
